/**
 * DisciplinesPage — hub for all 12 estimation disciplines.
 *
 * Left rail: discipline list (from shared config).
 * Tabs: Detailed Estimation (scope-item takeoff) | Discipline Summary
 * (labour planning worksheet). Both are the same config-driven components
 * used by the per-discipline menu items.
 */

import React, { useState } from 'react';
import { useCbConfig } from './cbApi';
import { LoadingState, NoEstimateState } from './CbUi';
import DisciplineDetailSheet from './DisciplineDetailSheet';
import DisciplineSummarySheet from './DisciplineSummarySheet';

const DisciplinesPage = ({ estimate }) => {
  const { config, loading } = useCbConfig();
  const [activeDiscipline, setActiveDiscipline] = useState('piping');
  const [activeTab, setActiveTab] = useState('detail');

  if (loading) return <LoadingState label="Loading disciplines…" />;
  if (!estimate?.id) return <NoEstimateState section="Disciplines" />;

  const disciplines = config?.disciplines || [];
  const current = disciplines.find((d) => d.key === activeDiscipline) || disciplines[0];

  return (
    <div style={{ height: '100%', display: 'flex', overflow: 'hidden' }}>
      {/* Discipline rail */}
      <div style={{ width: 210, flexShrink: 0, background: '#0f172a', color: '#fff', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
        <div style={{ padding: '16px 16px 10px', fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: '#94a3b8' }}>
          Disciplines
        </div>
        {disciplines.map((d) => {
          const isActive = d.key === current?.key;
          return (
            <button
              key={d.key}
              onClick={() => setActiveDiscipline(d.key)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                padding: '10px 16px', border: 'none', textAlign: 'left', cursor: 'pointer',
                background: isActive ? d.color : 'transparent',
                color: isActive ? '#fff' : '#cbd5e1',
                fontSize: 13, fontWeight: isActive ? 600 : 400,
                borderLeft: isActive ? '4px solid #fff' : '4px solid transparent',
                transition: 'all 0.15s',
              }}
            >
              <span style={{ fontSize: 16 }}>{d.icon}</span>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.label}</span>
            </button>
          );
        })}
      </div>

      {/* Content */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ display: 'flex', gap: 0, background: '#fff', borderBottom: '2px solid #e5e7eb', flexShrink: 0 }}>
          {[
            { key: 'detail', label: '🔍 Detailed Estimation' },
            { key: 'summary', label: '📊 Discipline Summary' },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                padding: '12px 24px', border: 'none', cursor: 'pointer', fontSize: 13.5, fontWeight: 600,
                background: activeTab === tab.key ? (current?.color || '#2563eb') : '#fff',
                color: activeTab === tab.key ? '#fff' : '#6b7280',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div style={{ flex: 1, overflow: 'hidden' }}>
          {activeTab === 'detail' ? (
            <DisciplineDetailSheet
              estimate={estimate}
              discipline={current?.key}
              onOpenSummary={() => setActiveTab('summary')}
            />
          ) : (
            <DisciplineSummarySheet
              estimate={estimate}
              discipline={current?.key}
              onOpenDetail={() => setActiveTab('detail')}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default DisciplinesPage;
